import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Plus, Pencil, Trash2, X, Check, RefreshCw, Filter, Loader2, Download, Calendar,
  Settings, Info, Wrench, AlertCircle, Target,
} from "lucide-react";

/**
 * Admin → Productivity tab.
 *
 * Per-day per-agent per-campaign report. `Target Achieved` is auto-derived
 * from the `leads` collection (count of leads created by that agent for that
 * campaign on that date) so it updates within the 10s poll whenever an agent
 * adds a lead. Quality Passed is manual (QA enters after review). Achieved %
 * = Quality Passed / Expected Target × 100; % Short = 100 − Achieved %.
 *
 * Styling follows the user-provided screenshot: deep-blue header band,
 * red < 50 % · yellow 50-79 % · green ≥ 80 % cell colour coding.
 */
const HEADER_BG = "#2A2EB0"; // matches screenshot's deep indigo

const BILLABLE_OPTS = ["Billable", "Non Billable"];

// Sticky-column geometry — both width AND left offset MUST be hard-pinned
// so position: sticky has stable px values regardless of content length.
const STICKY_COLS = {
  fte:  { width: 110, left: 0 },
  date: { width: 140, left: 110 },
};

export default function AdminProductivity({ readOnly = false }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState({});
  const [filters, setFilters] = useState({});
  const [dateFilter, setDateFilter] = useState("");
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [lookups, setLookups] = useState({ campaign_types: [], agent_types: [], campaign_ids: [], fte_values: [] });
  const [lookupsOpen, setLookupsOpen] = useState(false);
  // 2026-07-01: one-click "Repair Productivity" admin tool. Runs the
  // smart credit-backfill + auto-creates productivity rows for any
  // (agent, date, campaign) bucket that has credited leads but no
  // existing row. EXISTING rows are never overwritten.
  const [repairOpen, setRepairOpen] = useState(false);
  // 2026-07-15: "Resync Targets" — force-aligns `expected_target` on
  // every productivity row in the current filter against the CURRENT
  // campaign.type_targets. Fixes the bug where an auto-created row
  // shows target=0 because the campaign only defines per-type targets
  // (not the legacy single value). Idempotent.
  const [resyncBusy, setResyncBusy] = useState(false);

  const resyncTargets = async () => {
    if (!window.confirm(
      `Re-align expected_target on every productivity row visible in the current filter ` +
      `(${dateFilter || "ALL dates"}${filters.campaign_name ? " · " + filters.campaign_name : ""}) ` +
      `against the CURRENT campaign type_targets? Manual per-row target edits will be overwritten.`
    )) return;
    setResyncBusy(true);
    try {
      const params = {};
      if (dateFilter) { params.date_from = dateFilter; params.date_to = dateFilter; }
      if (filters.campaign_name) params.campaign_name = filters.campaign_name;
      const { data } = await api.post("/productivity/resync-targets", null, { params });
      toast.success(
        `Resynced ${data.updated} of ${data.scanned} rows` +
        (data.updated ? ` — ${Object.keys(data.by_campaign_type || {}).length} campaign/type buckets touched` : "")
      );
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Resync failed");
    } finally {
      setResyncBusy(false);
    }
  };

  const loadLookups = async () => {
    try {
      const { data } = await api.get("/productivity/lookups");
      setLookups(data || {});
    } catch { /* non-fatal */ }
  };

  useEffect(() => { loadLookups(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      // Single-date filter (matches the per-day row model — admins almost
      // always want one snapshot date, not a range).
      if (dateFilter) {
        params.set("date_from", dateFilter);
        params.set("date_to", dateFilter);
      }
      const { data } = await api.get(`/productivity?${params}`);
      setEntries(data.entries || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load productivity");
    } finally { setLoading(false); }
  };

  const loadOptions = async () => {
    try {
      const { data } = await api.get("/productivity/options");
      setOptions(data || {});
    } catch { /* non-fatal */ }
  };

  useEffect(() => { load(); loadOptions(); }, [dateFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-poll every 10s for real-time Target Achieved updates (paused when
  // the tab is hidden so background tabs don't hammer the API). Also
  // refreshes filter `options` so a newly-added user/campaign surfaces in
  // the dropdowns within 10s without a tab reload.
  useEffect(() => {
    const tick = () => {
      if (document.hidden) return;
      load();
      loadOptions();
    };
    const id = setInterval(tick, 10000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Client-side filter chain — all selected filters AND'd together.
  // Filters have been removed from the UI per the latest admin request —
  // the table now shows EVERY row in the collection. If date-scoped views
  // are needed later, expose the controls again here and wire date_from /
  // date_to back into load().
  const filtered = entries;

  // Team Meetings comments: fetch summary per unique date visible in the
  // table, keyed by "<agent_id>_<date>" for O(1) lookup at render time.
  // Empty when no meeting attendance recorded — column falls back to "—".
  const [meetingComments, setMeetingComments] = useState({});
  useEffect(() => {
    const dates = Array.from(new Set(filtered.map((r) => r.date).filter(Boolean)));
    if (dates.length === 0) { setMeetingComments({}); return; }
    let cancelled = false;
    (async () => {
      const map = {};
      // ONE batched range call instead of ~40 per-date requests — the
      // backend rolls up every day in [min, max] in a single query.
      const sorted = [...dates].sort();
      try {
        const { data } = await api.get("/team-meetings/summary-range", {
          params: { date_from: sorted[0], date_to: sorted[sorted.length - 1] },
        });
        for (const [date, users] of Object.entries(data || {})) {
          for (const [uid, info] of Object.entries(users || {})) {
            map[`${uid}_${date}`] = info?.comment || "";
          }
        }
      } catch { /* column falls back to "—" */ }
      if (!cancelled) setMeetingComments(map);
    })();
    return () => { cancelled = true; };
  }, [filtered]);

  const remove = async (id) => {
    if (!window.confirm("Delete this row?")) return;
    try {
      await api.delete(`/productivity/${id}`);
      toast.success("Row deleted");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  const exportCsv = () => {
    const cols = [
      "fte", "date", "billable", "campaign_name", "campaign_id", "campaign_type",
      "agent_name", "agent_type", "expected_target", "target_achieved",
      "quality_passed", "achieved_pct", "short_pct", "leave_days",
      "process_spoc", "process_tl", "process_owner", "lead_qualified_by", "over_time",
      "meeting_comment",
    ];
    const esc = (v) => {
      if (v === null || v === undefined) return "";
      const s = String(v).replace(/"/g, '""');
      return /[",\n]/.test(s) ? `"${s}"` : s;
    };
    const lines = [cols.join(",")];
    for (const r of filtered) lines.push(cols.map((c) => esc(r[c])).join(","));
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `productivity-${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4">
      {/* Title row */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <h2 className="font-display text-2xl font-bold">Productivity</h2>
          <p className="text-xs text-muted-foreground mt-1">
            Per-day per-agent per-campaign report. <strong>Target Achieved</strong> auto-counts from the leads database every 10 s.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {!readOnly && (
            <button onClick={() => { setEditing(null); setEditorOpen(true); }} data-testid="prod-new" className="btn-primary text-xs inline-flex items-center gap-1">
              <Plus size={11} /> New row
            </button>
          )}
          <button onClick={load} disabled={loading} data-testid="prod-refresh" className="btn-secondary text-xs inline-flex items-center gap-1 disabled:opacity-50">
            <RefreshCw size={11} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
          <button onClick={exportCsv} disabled={!filtered.length} data-testid="prod-export" className="btn-secondary text-xs inline-flex items-center gap-1 disabled:opacity-50">
            <Download size={11} /> CSV
          </button>
          {!readOnly && (
            <button onClick={() => setLookupsOpen(true)} data-testid="prod-manage-lookups" className="btn-secondary text-xs inline-flex items-center gap-1" title="Manage Campaign Type / Agent Type / FTE option lists">
              <Settings size={11} /> Lookups
            </button>
          )}
          {!readOnly && (
            <button
              onClick={resyncTargets}
              disabled={resyncBusy}
              data-testid="prod-resync-targets"
              className="text-xs inline-flex items-center gap-1 px-2.5 py-1.5 rounded-sm bg-indigo-600 hover:bg-indigo-700 text-white font-medium disabled:opacity-50"
              title="Force-align expected_target on every row (current filter) against the CURRENT campaign type_targets. Idempotent — safe to run repeatedly."
            >
              {resyncBusy ? <Loader2 size={11} className="animate-spin" /> : <Target size={11} />}
              Resync Targets
            </button>
          )}
          {!readOnly && (
            <button
              onClick={() => setRepairOpen(true)}
              data-testid="prod-repair"
              className="text-xs inline-flex items-center gap-1 px-2.5 py-1.5 rounded-sm bg-amber-500 hover:bg-amber-600 text-white font-medium"
              title="One-click repair: backfill agent credit on historical bulk-uploaded leads + auto-create missing productivity rows"
            >
              <Wrench size={11} /> Repair Productivity
            </button>
          )}
          {readOnly && (
            <span data-testid="prod-readonly-badge" className="text-[10px] uppercase tracking-wider px-2 py-1 rounded-sm badge-info">
              View only
            </span>
          )}
        </div>
      </div>

      {/* Formula card — surfaces the math behind the % columns so admins
          can sanity-check the auto-computed values at a glance. */}
      <div className="border border-sky-200 bg-sky-50 rounded-sm px-4 py-3 text-xs space-y-1" data-testid="prod-formula-card">
        <div className="flex items-center gap-1.5 font-semibold text-sky-900">
          <Info size={13} /> How the % columns are computed
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1 mt-1">
          <div data-testid="prod-formula-achieved">
            <span className="font-mono bg-white px-1.5 py-0.5 rounded-sm border border-sky-200">Achieved %</span>
            <span className="mx-1 text-muted-foreground">=</span>
            <span className="font-mono">Quality Passed</span>
            <span className="mx-1 text-muted-foreground">÷</span>
            <span className="font-mono">Expected Target</span>
            <span className="mx-1 text-muted-foreground">×</span>
            <span className="font-mono">100</span>
          </div>
          <div data-testid="prod-formula-short">
            <span className="font-mono bg-white px-1.5 py-0.5 rounded-sm border border-sky-200">% Short</span>
            <span className="mx-1 text-muted-foreground">=</span>
            <span className="font-mono">max(0,</span>
            <span className="font-mono mx-1">100 − Achieved %</span>
            <span className="font-mono">)</span>
          </div>
        </div>
        <div className="text-[11px] text-muted-foreground pt-1">
          Note: <strong>Target Achieved</strong> (auto-counted from leads) is for monitoring only —
          the % columns intentionally use <strong>Quality Passed</strong> so QA-rejected leads
          don&apos;t inflate an agent&apos;s score. If Quality Passed is 0, Achieved % is 0 even when
          Target Achieved is non-zero.
        </div>
      </div>

      {/* Table — filters live directly inside the blue header (single visual block).
          Outer container allows BOTH axes to scroll: vertical (max-h) keeps the
          sticky thead pinned to the top while admins scan many rows; horizontal
          scroll surfaces the right-hand columns while the first column (FTE) stays
          frozen on the left. */}
      <div className="max-h-[70vh] overflow-auto border border-border rounded-sm relative">
        <table className="w-full text-xs">
          <thead className="sticky top-0 z-30">
            {/* Filters removed per latest admin spec — only the column-
                labels row remains. FTE + Date stay sticky-left so admins
                can still scan wide tables horizontally. */}
            <tr className="text-white" style={{ backgroundColor: HEADER_BG }}>
              <ThLabel stickyKey="fte">FTE</ThLabel>
              <ThLabel stickyKey="date">Date</ThLabel>
              <ThLabel>Billable / Non Billable</ThLabel>
              <ThLabel>Campaign Name</ThLabel>
              <ThLabel>Campaign ID</ThLabel>
              <ThLabel>Campaign Type</ThLabel>
              <ThLabel>Agent Name</ThLabel>
              <ThLabel>Agent Type</ThLabel>
              <ThLabel tight title="Daily target the agent must hit (set in Productivity Defaults on the Campaign).">
                <>Expected<br />Targets</>
              </ThLabel>
              <ThLabel tight title="Auto-counted from leads created by this agent on this campaign on this date.">
                <>Target<br />Achieved</>
              </ThLabel>
              <ThLabel tight title="Manual — entered by QA after audit. Used as the numerator for Achieved %.">
                <>Quality<br />Passed</>
              </ThLabel>
              <ThLabel tight title="Achieved % = Quality Passed ÷ Expected Target × 100">Achieved %</ThLabel>
              <ThLabel tight title="% Short = max(0, 100 − Achieved %)">% Short</ThLabel>
              <ThLabel>Leave</ThLabel>
              <ThLabel>Process SPOC</ThLabel>
              <ThLabel>Process TL</ThLabel>
              <ThLabel>Process Owner</ThLabel>
              <ThLabel>Lead Qualified by</ThLabel>
              <ThLabel>Over Time</ThLabel>
              <ThLabel title="Auto-populated from Team Meetings (Meeting / Training minutes attended on this date)">Comments</ThLabel>
              <ThLabel tight />
            </tr>
          </thead>
          <tbody>
            {loading && filtered.length === 0 && (
              <tr><td colSpan={21} className="p-6 text-center text-muted-foreground bg-card"><Loader2 size={12} className="animate-spin inline mr-1" /> Loading…</td></tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={21} className="p-8 text-center text-muted-foreground bg-card">No productivity rows yet. Click <strong>New row</strong> to add one.</td></tr>
            )}
            {filtered.map((r) => (
              <tr key={r.id} data-testid={`prod-row-${r.id}`} className="group border-t border-border hover:bg-secondary/30">
                <Td stickyKey="fte">{r.fte ?? "—"}</Td>
                <Td stickyKey="date">{formatDate(r.date)}</Td>
                <Td>{r.billable || "—"}</Td>
                <Td>{r.campaign_name || "—"}</Td>
                <Td>{r.campaign_id || "—"}</Td>
                <Td>{r.campaign_type || "—"}</Td>
                <Td>{r.agent_name || "—"}</Td>
                <Td>{r.agent_type || "—"}</Td>
                <Td className="text-right tabular-nums">{r.expected_target ?? "—"}</Td>
                <Td className="text-right tabular-nums font-semibold" data-testid={`prod-target-achieved-${r.id}`}>{r.target_achieved ?? 0}</Td>
                <Td className="text-right tabular-nums">{r.quality_passed ?? 0}</Td>
                <PctCell value={r.achieved_pct} testid={`prod-achieved-${r.id}`} />
                <PctCell value={r.short_pct} invert testid={`prod-short-${r.id}`} />
                <Td className="text-right tabular-nums">{r.leave_days || 0}</Td>
                <Td>{r.process_spoc || "—"}</Td>
                <Td>{r.process_tl || "—"}</Td>
                <Td>{r.process_owner || "—"}</Td>
                <Td>{r.lead_qualified_by || "—"}</Td>
                <Td className="text-right tabular-nums">{r.over_time || 0}</Td>
                <Td className="text-xs text-muted-foreground" data-testid={`prod-comment-${r.id}`}>
                  {(meetingComments[`${r.agent_id || ""}_${r.date || ""}`] || "").trim() || "—"}
                </Td>
                <td className="px-2 py-1.5 whitespace-nowrap">
                  {readOnly ? (
                    <span className="text-[10px] text-muted-foreground italic" data-testid={`prod-readonly-row-${r.id}`}>—</span>
                  ) : (
                    <>
                      <button onClick={() => { setEditing(r); setEditorOpen(true); }} data-testid={`prod-edit-${r.id}`} className="text-primary hover:underline mr-2"><Pencil size={11} /></button>
                      <button onClick={() => remove(r.id)} data-testid={`prod-delete-${r.id}`} className="text-red-600 hover:underline"><Trash2 size={11} /></button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editorOpen && (
        <ProductivityEditor
          initial={editing}
          options={options}
          lookups={lookups}
          onClose={() => { setEditorOpen(false); setEditing(null); }}
          onSaved={() => { setEditorOpen(false); setEditing(null); load(); loadOptions(); }}
        />
      )}
      {lookupsOpen && (
        <LookupsDialog
          lookups={lookups}
          onClose={() => setLookupsOpen(false)}
          onSaved={(next) => { setLookups(next); setLookupsOpen(false); toast.success("Lookups saved"); }}
        />
      )}
      {repairOpen && (
        <RepairProductivityDialog
          onClose={() => setRepairOpen(false)}
          onCommitted={() => { setRepairOpen(false); load(); }}
        />
      )}
    </div>
  );
}

// ─────────────────────────── Header + Filter row helpers ─────────────────
// We render the column LABEL row (blue) and the FILTER INPUT row (light)
// as two SEPARATE rows inside the sticky <thead>. This way the filter
// inputs visually align with the data rows below them rather than blending
// into the indigo header band.

function ThLabel({ children, tight, stickyKey, title }) {
  const cfg = stickyKey ? STICKY_COLS[stickyKey] : null;
  const stickyCls = cfg
    ? "sticky z-20 shadow-[1px_0_0_rgba(255,255,255,0.18)]"
    : "";
  const style = cfg
    ? { backgroundColor: HEADER_BG, left: cfg.left, width: cfg.width, minWidth: cfg.width }
    : undefined;
  return (
    <th
      className={`text-[10px] uppercase tracking-wide font-semibold text-white ${tight ? "px-2" : "px-3"} py-2 text-left whitespace-nowrap ${stickyCls} ${title ? "cursor-help underline decoration-dotted underline-offset-4 decoration-white/40" : ""}`}
      style={style}
      title={title}
    >
      {children}
    </th>
  );
}

function TdFilter({ children, tight, stickyKey }) {
  const cfg = stickyKey ? STICKY_COLS[stickyKey] : null;
  // Sticky filter cells need a SOLID bg (the non-sticky cells use
  // bg-secondary/40 — but transparency leaks during horizontal scroll, so
  // we paint sticky-left cells with a matching solid tone).
  const stickyCls = cfg
    ? "sticky z-20 bg-slate-200 shadow-[1px_0_0_rgba(0,0,0,0.06)]"
    : "";
  const style = cfg
    ? { left: cfg.left, width: cfg.width, minWidth: cfg.width }
    : undefined;
  return (
    <td
      className={`${tight ? "px-2" : "px-3"} py-1.5 align-middle ${stickyCls}`}
      style={style}
    >
      {children}
    </td>
  );
}

// Light-themed filter controls (used in the filter row UNDER the blue header).
function FilterSelect({ value, onChange, options, testid }) {
  return (
    <select
      data-testid={testid}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="form-input h-7 w-full min-w-[100px] max-w-[160px] text-[11px] px-1.5"
    >
      <option value="">All</option>
      {options.map((v) => (
        <option key={v} value={v}>{v}</option>
      ))}
    </select>
  );
}

function FilterDate({ value, onChange, testid }) {
  return (
    <input
      type="date"
      data-testid={testid}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="form-input h-7 w-full min-w-[120px] text-[11px] px-1.5"
    />
  );
}

function FilterNumber({ value, onChange, testid }) {
  return (
    <input
      type="number"
      data-testid={testid}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder="Any"
      className="form-input h-7 w-full min-w-[70px] max-w-[90px] text-[11px] px-1.5"
    />
  );
}

function Td({ children, className = "", stickyKey, ...rest }) {
  // Sticky cells need an opaque background so neighbour cells don't bleed
  // through during horizontal scroll. Uses group-hover (set on the parent
  // <tr> via className="group") to mirror the hover-bg seen on the rest of
  // the row.
  const cfg = stickyKey ? STICKY_COLS[stickyKey] : null;
  const stickyCls = cfg
    ? "sticky z-10 bg-card group-hover:bg-secondary/30 shadow-[1px_0_0_rgba(0,0,0,0.06)]"
    : "";
  const style = cfg
    ? { left: cfg.left, width: cfg.width, minWidth: cfg.width }
    : undefined;
  return (
    <td
      className={`px-3 py-1.5 whitespace-nowrap ${stickyCls} ${className}`}
      style={style}
      {...rest}
    >
      {children}
    </td>
  );
}

function PctCell({ value, invert = false, testid }) {
  // invert=true means a HIGH number is BAD (the % Short column).
  if (value === null || value === undefined) {
    return <td data-testid={testid} className="px-2 py-1.5 text-center bg-emerald-100 text-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-300 font-semibold">N/A</td>;
  }
  // Color scheme matching the screenshot:
  // green ≥80%, yellow 50-79%, red <50% (normal direction)
  // inverted (% Short): green ≤20%, yellow 21-50%, red >50%
  let bg, fg;
  if (!invert) {
    if (value >= 80)      { bg = "bg-emerald-200 dark:bg-emerald-900/50"; fg = "text-emerald-900 dark:text-emerald-200"; }
    else if (value >= 50) { bg = "bg-yellow-200 dark:bg-yellow-900/40";   fg = "text-yellow-900 dark:text-yellow-200"; }
    else                  { bg = "bg-red-300 dark:bg-red-900/50";         fg = "text-red-900 dark:text-red-200"; }
  } else {
    if (value <= 20)      { bg = "bg-emerald-200 dark:bg-emerald-900/50"; fg = "text-emerald-900 dark:text-emerald-200"; }
    else if (value <= 50) { bg = "bg-yellow-200 dark:bg-yellow-900/40";   fg = "text-yellow-900 dark:text-yellow-200"; }
    else                  { bg = "bg-red-300 dark:bg-red-900/50";         fg = "text-red-900 dark:text-red-200"; }
  }
  return <td data-testid={testid} className={`px-2 py-1.5 text-center font-semibold ${bg} ${fg}`}>{value}%</td>;
}

function formatDate(s) {
  if (!s) return "—";
  try {
    const d = new Date(s + "T00:00:00Z");
    return d.toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" });
  } catch { return s; }
}

// ─────────────────────── Editor dialog ───────────────────────
function ProductivityEditor({ initial, options, lookups, onClose, onSaved }) {
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    fte: 1, date: today, billable: "Billable",
    campaign_name: "", campaign_id: "", campaign_type: "",
    agent_name: "", agent_type: "Lead",
    expected_target: 0, quality_passed: 0, leave_days: 0,
    process_spoc: "", process_tl: "", process_owner: "",
    lead_qualified_by: "", over_time: 0,
    ...(initial || {}),
  });
  const [saving, setSaving] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [agents, setAgents] = useState([]);

  useEffect(() => {
    // Pull campaign + agent suggestions to feed the auto-complete selects.
    // We use /users/picklist (allowed for any admin sub-page user) so a
    // custom role with only `admin_productivity` can still see agent
    // names when creating / editing a row. /users would 403 since it
    // requires `admin_users`.
    (async () => {
      try {
        const [c, u] = await Promise.all([
          api.get("/campaigns").catch(() => ({ data: [] })),
          api.get("/users/picklist")
            .catch(() => api.get("/users").catch(() => ({ data: [] }))),
        ]);
        setCampaigns(c.data || []);
        setAgents((u.data || []).filter((x) => (x.role || "").toLowerCase() === "agent"));
      } catch { /* */ }
    })();
  }, []);

  const set = (k) => (e) => {
    const v = e?.target ? (e.target.type === "number" ? Number(e.target.value) : e.target.value) : e;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const save = async () => {
    if (!form.date) { toast.error("Date is required"); return; }
    if (!form.campaign_name) { toast.error("Campaign name is required"); return; }
    if (!form.agent_name) { toast.error("Agent name is required"); return; }
    setSaving(true);
    try {
      if (initial?.id) {
        await api.put(`/productivity/${initial.id}`, form);
        toast.success("Row updated");
      } else {
        await api.post("/productivity", form);
        toast.success("Row added");
      }
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  // Local achieved-pct preview so the admin sees the formula working as they type.
  const expected = Number(form.expected_target) || 0;
  const quality = Number(form.quality_passed) || 0;
  const previewPct = expected > 0 ? Math.round((quality / expected) * 1000) / 10 : null;

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="prod-editor-backdrop">
      <div onClick={(e) => e.stopPropagation()} data-testid="prod-editor" className="bg-card border border-border rounded-sm w-full max-w-3xl max-h-[90vh] flex flex-col">
        <div className="p-5 border-b border-border flex items-start justify-between" style={{ backgroundColor: HEADER_BG, color: "white" }}>
          <div>
            <div className="text-[10px] uppercase tracking-wide opacity-80">Productivity row</div>
            <h3 className="font-display text-lg font-bold mt-0.5">{initial?.id ? "Edit row" : "Add row"}</h3>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-sm" data-testid="prod-editor-close"><X size={14} /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-5 grid grid-cols-2 md:grid-cols-3 gap-3 text-xs">
          <Field label="Date" required>
            <div className="relative">
              <Calendar size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
              <input type="date" value={form.date} onChange={set("date")} className="form-input w-full pl-8 text-xs" data-testid="prod-edit-date" />
            </div>
          </Field>
          <Field label="FTE">
            {lookups?.fte_values?.length ? (
              <select value={form.fte} onChange={set("fte")} className="form-input w-full text-xs" data-testid="prod-edit-fte">
                {lookups.fte_values.map((v) => <option key={v} value={v}>{v}</option>)}
              </select>
            ) : (
              <input type="number" step="0.1" value={form.fte} onChange={set("fte")} className="form-input w-full text-xs" data-testid="prod-edit-fte" />
            )}
          </Field>
          <Field label="Billable / Non Billable">
            <select value={form.billable} onChange={set("billable")} className="form-input w-full text-xs" data-testid="prod-edit-billable">
              {BILLABLE_OPTS.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          </Field>
          <Field label="Campaign Name" required>
            <input list="prod-campaigns" value={form.campaign_name} onChange={set("campaign_name")} className="form-input w-full text-xs" data-testid="prod-edit-campaign-name" />
            <datalist id="prod-campaigns">
              {campaigns.map((c) => <option key={c.id} value={c.name} />)}
            </datalist>
          </Field>
          <Field label="Campaign ID">
            {lookups?.campaign_ids?.length ? (
              <select value={form.campaign_id} onChange={set("campaign_id")} className="form-input w-full text-xs" data-testid="prod-edit-campaign-id">
                <option value="">—</option>
                {lookups.campaign_ids.map((v) => <option key={v} value={v}>{v}</option>)}
              </select>
            ) : (
              <input value={form.campaign_id} onChange={set("campaign_id")} className="form-input w-full text-xs" data-testid="prod-edit-campaign-id" />
            )}
          </Field>
          <Field label="Campaign Type">
            {lookups?.campaign_types?.length ? (
              <select value={form.campaign_type} onChange={set("campaign_type")} className="form-input w-full text-xs" data-testid="prod-edit-campaign-type">
                <option value="">—</option>
                {lookups.campaign_types.map((v) => <option key={v} value={v}>{v}</option>)}
              </select>
            ) : (
              <input value={form.campaign_type} onChange={set("campaign_type")} className="form-input w-full text-xs" data-testid="prod-edit-campaign-type" />
            )}
          </Field>
          <Field label="Agent Name" required>
            <input list="prod-agents" value={form.agent_name} onChange={set("agent_name")} className="form-input w-full text-xs" data-testid="prod-edit-agent-name" />
            <datalist id="prod-agents">
              {agents.map((a) => <option key={a.id} value={a.name} />)}
            </datalist>
          </Field>
          <Field label="Agent Type">
            <select value={form.agent_type} onChange={set("agent_type")} className="form-input w-full text-xs" data-testid="prod-edit-agent-type">
              {(lookups?.agent_types?.length ? lookups.agent_types : ["Lead", "Data", "QA", "Team Lead"]).map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          </Field>
          <Field label="Expected Target"><input type="number" min={0} value={form.expected_target} onChange={set("expected_target")} className="form-input w-full text-xs" data-testid="prod-edit-expected" /></Field>
          <Field label="Quality Passed (auto)"><input type="number" min={0} value={form.quality_passed} disabled className="form-input w-full text-xs bg-muted/50 cursor-not-allowed" data-testid="prod-edit-quality" title="Auto-computed from QA-Approved leads created by this agent on this campaign+date." /></Field>
          <Field label="Leave (days)"><input type="number" step="0.5" min={0} value={form.leave_days} onChange={set("leave_days")} className="form-input w-full text-xs" data-testid="prod-edit-leave" /></Field>
          <Field label="Over Time (hours)"><input type="number" step="0.5" min={0} value={form.over_time} onChange={set("over_time")} className="form-input w-full text-xs" data-testid="prod-edit-overtime" /></Field>
          <Field label="Process SPOC"><input value={form.process_spoc} onChange={set("process_spoc")} className="form-input w-full text-xs" data-testid="prod-edit-spoc" /></Field>
          <Field label="Process TL"><input value={form.process_tl} onChange={set("process_tl")} className="form-input w-full text-xs" data-testid="prod-edit-tl" /></Field>
          <Field label="Process Owner"><input value={form.process_owner} onChange={set("process_owner")} className="form-input w-full text-xs" data-testid="prod-edit-owner" /></Field>
          <Field label="Lead Qualified by"><input value={form.lead_qualified_by} onChange={set("lead_qualified_by")} className="form-input w-full text-xs" data-testid="prod-edit-qualified-by" /></Field>
          <div className="md:col-span-3 border-t border-border pt-2 text-[11px] text-muted-foreground">
            <strong>Live preview:</strong> Achieved % = {previewPct === null ? "N/A (Expected Target = 0)" : `${previewPct}%`}
            {previewPct !== null && (
              <span className="ml-2">· % Short = <strong>{Math.max(0, 100 - previewPct).toFixed(1)}%</strong></span>
            )}
            <span className="ml-2 italic">Target Achieved &amp; Quality Passed auto-count from leads added by this agent for this campaign on this date (Quality Passed = leads marked QA-Approved).</span>
          </div>
        </div>
        <div className="p-4 border-t border-border flex items-center justify-end gap-2">
          <button onClick={onClose} className="btn-secondary" data-testid="prod-editor-cancel">Cancel</button>
          <button onClick={save} disabled={saving} className="btn-primary inline-flex items-center gap-1 disabled:opacity-50" data-testid="prod-editor-save">
            {saving ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
            {saving ? "Saving…" : "Save row"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children, required }) {
  return (
    <div>
      <label className="meta-label text-[10px]">
        {label}{required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      <div className="mt-0.5">{children}</div>
    </div>
  );
}

// ─────────────────────── Lookups manager ───────────────────────
function LookupsDialog({ lookups, onClose, onSaved }) {
  const [campaignTypes, setCampaignTypes] = useState(lookups.campaign_types || []);
  const [agentTypes, setAgentTypes] = useState(lookups.agent_types || []);
  const [campaignIds, setCampaignIds] = useState(lookups.campaign_ids || []);
  const [fteValues, setFteValues] = useState(lookups.fte_values || []);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/productivity/lookups", {
        campaign_types: campaignTypes.map((s) => String(s).trim()).filter(Boolean),
        agent_types: agentTypes.map((s) => String(s).trim()).filter(Boolean),
        campaign_ids: campaignIds.map((s) => String(s).trim()).filter(Boolean),
        fte_values: fteValues
          .map((v) => Number(v))
          .filter((n) => !Number.isNaN(n) && n >= 0),
      });
      onSaved(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="prod-lookups-backdrop">
      <div onClick={(e) => e.stopPropagation()} data-testid="prod-lookups-dialog" className="bg-card border border-border rounded-sm w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="p-5 border-b border-border flex items-start justify-between" style={{ backgroundColor: HEADER_BG, color: "white" }}>
          <div>
            <div className="text-[10px] uppercase tracking-wide opacity-80">Productivity</div>
            <h3 className="font-display text-lg font-bold mt-0.5">Manage option lists</h3>
            <p className="text-[11px] opacity-80 mt-1">Add or remove values that appear in the row editor&apos;s dropdowns. Campaign Type also powers the Add-Lead form so the two screens stay in sync.</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-sm" data-testid="prod-lookups-close"><X size={14} /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          <LookupList
            label="Campaign Type (shared with Add-Lead form)"
            values={campaignTypes}
            setValues={setCampaignTypes}
            placeholder="e.g. LG"
            testidPrefix="campaign-type"
          />
          <LookupList
            label="Campaign ID"
            values={campaignIds}
            setValues={setCampaignIds}
            placeholder="e.g. VMSBT001"
            testidPrefix="campaign-id"
          />
          <LookupList
            label="Agent Type"
            values={agentTypes}
            setValues={setAgentTypes}
            placeholder="e.g. Lead"
            testidPrefix="agent-type"
          />
          <LookupList
            label="FTE values"
            values={fteValues}
            setValues={setFteValues}
            placeholder="e.g. 1.0"
            numeric
            testidPrefix="fte"
          />
        </div>
        <div className="p-4 border-t border-border flex items-center justify-end gap-2">
          <button onClick={onClose} className="btn-secondary" data-testid="prod-lookups-cancel">Cancel</button>
          <button onClick={save} disabled={saving} className="btn-primary inline-flex items-center gap-1 disabled:opacity-50" data-testid="prod-lookups-save">
            {saving ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
            {saving ? "Saving…" : "Save lookups"}
          </button>
        </div>
      </div>
    </div>
  );
}

function LookupList({ label, values, setValues, placeholder, numeric = false, testidPrefix }) {
  const [draft, setDraft] = useState("");

  const add = () => {
    const v = numeric ? Number(draft) : draft.trim();
    if (numeric ? Number.isNaN(v) : !v) return;
    if (values.some((x) => String(x) === String(v))) {
      toast.message(`"${v}" already in the list`);
      setDraft("");
      return;
    }
    setValues([...values, v]);
    setDraft("");
  };

  return (
    <div>
      <label className="meta-label">{label}</label>
      <div className="mt-1 flex flex-wrap gap-1.5" data-testid={`lookup-list-${testidPrefix}`}>
        {values.length === 0 && (
          <span className="text-[11px] text-muted-foreground italic">No values yet — add one below.</span>
        )}
        {values.map((v, i) => (
          <span key={`${v}-${i}`} className="inline-flex items-center gap-1 border border-border bg-secondary/40 rounded-sm px-2 py-0.5 text-xs">
            <span className="font-mono">{String(v)}</span>
            <button
              onClick={() => setValues(values.filter((_, j) => j !== i))}
              data-testid={`lookup-remove-${testidPrefix}-${i}`}
              className="text-red-600 hover:bg-red-50 dark:hover:bg-red-950/40 rounded-sm"
              title="Remove"
            >
              <X size={10} />
            </button>
          </span>
        ))}
      </div>
      <div className="mt-2 flex gap-1.5">
        <input
          type={numeric ? "number" : "text"}
          step={numeric ? "0.1" : undefined}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
          placeholder={placeholder}
          className="form-input flex-1 text-xs"
          data-testid={`lookup-input-${testidPrefix}`}
        />
        <button onClick={add} className="btn-secondary text-xs inline-flex items-center gap-1" data-testid={`lookup-add-${testidPrefix}`}>
          <Plus size={10} /> Add
        </button>
      </div>
    </div>
  );
}


// ───────────── Repair Productivity dialog (2026-07-01) ─────────────
// One-click admin tool:
//   Step 1  Smart credit-backfill — re-stamps `created_by_id` on leads
//           whose `agent_name` matches a real user (exact → prefix →
//           contains waterfall, single-match guard).
//   Step 2  Auto-creates productivity rows for every (agent_id,
//           normalized_date, campaign_name) bucket that now has
//           credited leads but no existing row. EXISTING rows are
//           left alone (preserves admin's expected_target, FTE, etc.).
//
// Always runs in DRY-RUN first so admin can preview the report; a
// confirmation step then commits the changes.
function RepairProductivityDialog({ onClose, onCommitted }) {
  const [running, setRunning] = useState(false);
  const [report, setReport] = useState(null);
  const [committing, setCommitting] = useState(false);
  const [committed, setCommitted] = useState(false);
  const [matchMode, setMatchMode] = useState("smart");
  const [error, setError] = useState("");

  const runDry = async () => {
    setRunning(true); setError(""); setReport(null); setCommitted(false);
    try {
      const { data } = await api.post(
        `/leads/admin/repair-productivity?dry_run=1&match_mode=${matchMode}`,
      );
      setReport(data);
    } catch (e) {
      setError(e.response?.data?.detail || "Repair preview failed");
    } finally { setRunning(false); }
  };

  const commit = async () => {
    setCommitting(true); setError("");
    try {
      const { data } = await api.post(
        `/leads/admin/repair-productivity?dry_run=0&match_mode=${matchMode}`,
      );
      setReport(data);
      setCommitted(true);
      toast.success(
        `Repaired ${data?.backfill?.updated ?? 0} lead credits + created ${data?.productivity?.rows_created ?? 0} productivity rows`,
      );
    } catch (e) {
      setError(e.response?.data?.detail || "Repair commit failed");
    } finally { setCommitting(false); }
  };

  // Auto-run preview on open
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { runDry(); }, []);

  const bf = report?.backfill || {};
  const prod = report?.productivity || {};

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center pt-12 p-4 overflow-y-auto" data-testid="repair-productivity-dialog">
      <div className="bg-white rounded-md max-w-4xl w-full shadow-xl">
        {/* Header */}
        <div className="flex items-start justify-between p-5 border-b border-border">
          <div>
            <h3 className="font-display text-lg font-bold flex items-center gap-2">
              <Wrench size={16} className="text-amber-600" />
              Repair Productivity
            </h3>
            <p className="text-xs text-muted-foreground mt-1 max-w-2xl">
              Fixes historical bulk uploads where the lead credit went to the uploader (team lead) instead of the named agent,
              and auto-creates any missing productivity rows. Existing rows are <strong>never overwritten</strong>.
              Always previews changes first — nothing is written until you click <em>Commit</em>.
            </p>
          </div>
          <button onClick={onClose} data-testid="repair-close" className="text-muted-foreground hover:text-foreground"><X size={18} /></button>
        </div>

        {/* Body */}
        <div className="p-5 space-y-4 max-h-[70vh] overflow-y-auto">
          {/* Mode + actions */}
          <div className="flex flex-wrap items-center gap-3 text-xs">
            <label className="flex items-center gap-1.5">
              <span className="text-muted-foreground">Match mode:</span>
              <select
                value={matchMode}
                onChange={(e) => setMatchMode(e.target.value)}
                data-testid="repair-match-mode"
                className="border border-border rounded-sm px-2 py-1 text-xs"
                disabled={running || committing}
              >
                <option value="smart">smart (exact → prefix → contains)</option>
                <option value="exact">exact only (safest)</option>
                <option value="prefix">exact or prefix</option>
                <option value="contains">exact or contains</option>
              </select>
            </label>
            <button onClick={runDry} disabled={running || committing} data-testid="repair-rerun-dry" className="btn-secondary inline-flex items-center gap-1 text-xs disabled:opacity-50">
              {running ? <Loader2 size={11} className="animate-spin" /> : <RefreshCw size={11} />}
              Re-run preview
            </button>
          </div>

          {error && (
            <div className="border border-red-200 bg-red-50 text-red-800 text-xs rounded-sm px-3 py-2 flex items-start gap-2" data-testid="repair-error">
              <AlertCircle size={13} className="mt-0.5 shrink-0" />
              <div>{error}</div>
            </div>
          )}

          {running && !report && (
            <div className="text-center text-xs text-muted-foreground py-12">
              <Loader2 className="animate-spin inline mr-2" size={14} /> Running preview…
            </div>
          )}

          {report && (
            <>
              {/* Summary cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                <SummaryCard label="Leads scanned" value={bf.scanned ?? 0} testid="repair-sum-scanned" />
                <SummaryCard label="Credits to fix" value={bf.updated ?? 0} accent="amber" testid="repair-sum-updated" />
                <SummaryCard label="New prod rows" value={prod.rows_to_create ?? 0} accent="emerald" testid="repair-sum-prod-new" />
                <SummaryCard label="Existing rows preserved" value={prod.rows_skipped_existing ?? 0} testid="repair-sum-prod-kept" />
              </div>

              {(bf.unmatched || bf.ambiguous) ? (
                <div className="text-[11px] text-muted-foreground flex flex-wrap gap-3 mt-1" data-testid="repair-warnings">
                  {bf.unmatched > 0 && <span><strong>{bf.unmatched}</strong> leads have an agent_name that doesn&apos;t map to any user</span>}
                  {bf.ambiguous > 0 && <span>· <strong>{bf.ambiguous}</strong> ambiguous (multiple users match)</span>}
                </div>
              ) : null}

              {/* By-agent credit breakdown */}
              {Array.isArray(bf.by_agent) && bf.by_agent.length > 0 && (
                <div data-testid="repair-by-agent">
                  <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">Credits will be moved to:</div>
                  <div className="border border-border rounded-sm overflow-hidden">
                    <table className="w-full text-xs">
                      <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-muted-foreground">
                        <tr>
                          <th className="px-3 py-1.5 text-left">Agent</th>
                          <th className="px-3 py-1.5 text-right">Leads</th>
                          <th className="px-3 py-1.5 text-left">Matched via</th>
                        </tr>
                      </thead>
                      <tbody>
                        {bf.by_agent.map((b, i) => (
                          <tr key={`${b.agent_name}-${i}`} className="border-t border-border">
                            <td className="px-3 py-1.5 font-medium">{b.agent_name}</td>
                            <td className="px-3 py-1.5 text-right tabular-nums">{b.count}</td>
                            <td className="px-3 py-1.5">
                              <span className={`text-[10px] px-1.5 py-0.5 rounded-sm ${b.match_mode === "exact" ? "bg-emerald-100 text-emerald-800" : b.match_mode === "prefix" ? "bg-amber-100 text-amber-800" : "bg-blue-100 text-blue-800"}`}>
                                {b.match_mode}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* By-bucket productivity preview */}
              {Array.isArray(prod.by_bucket) && prod.by_bucket.length > 0 && (
                <div data-testid="repair-by-bucket">
                  <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">Productivity rows ({prod.by_bucket.length} buckets)</div>
                  <div className="border border-border rounded-sm overflow-hidden max-h-64 overflow-y-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-slate-50 sticky top-0 text-[11px] uppercase tracking-wider text-muted-foreground">
                        <tr>
                          <th className="px-3 py-1.5 text-left">Agent</th>
                          <th className="px-3 py-1.5 text-left">Date</th>
                          <th className="px-3 py-1.5 text-left">Campaign</th>
                          <th className="px-3 py-1.5 text-right">Leads</th>
                          <th className="px-3 py-1.5 text-left">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {prod.by_bucket.map((b, i) => (
                          <tr key={i} className="border-t border-border">
                            <td className="px-3 py-1.5">{b.agent_name}</td>
                            <td className="px-3 py-1.5 font-mono">{b.date}</td>
                            <td className="px-3 py-1.5">{b.campaign}</td>
                            <td className="px-3 py-1.5 text-right tabular-nums">{b.count}</td>
                            <td className="px-3 py-1.5">
                              <span className={`text-[10px] px-1.5 py-0.5 rounded-sm ${b.action?.startsWith("skipped") ? "bg-slate-100 text-slate-700" : "bg-emerald-100 text-emerald-800"}`}>
                                {b.action}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Ambiguous samples */}
              {Array.isArray(bf.ambiguous_samples) && bf.ambiguous_samples.length > 0 && (
                <div className="text-[11px] border border-amber-200 bg-amber-50 rounded-sm p-3" data-testid="repair-ambiguous">
                  <div className="font-semibold text-amber-900 mb-1">Ambiguous agent names (stayed with uploader):</div>
                  <ul className="space-y-0.5">
                    {bf.ambiguous_samples.map((s, i) => (
                      <li key={i}><strong>{s.agent_name}</strong> → could be {s.candidates?.join(" / ")}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Diagnostics — surfaces silent skips during bucket build.
                  When the modal showed e.g. "644 credits to fix" but
                  zero productivity rows were created, this panel reveals
                  why (unparseable date format, blank campaign, etc.). */}
              {report?.diagnostics && (
                <div className="text-[11px] border border-slate-200 bg-slate-50 rounded-sm p-3 space-y-1" data-testid="repair-diagnostics">
                  <div className="font-semibold text-slate-800 uppercase tracking-wider text-[10px] mb-1">Diagnostics</div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-1">
                    <div>Resolved (agent matched): <strong className="tabular-nums">{report.diagnostics.leads_with_resolved_agent ?? 0}</strong></div>
                    <div>Bucketed for productivity: <strong className="tabular-nums text-emerald-700">{report.diagnostics.leads_bucketed ?? 0}</strong></div>
                    <div>Skipped — blank campaign: <strong className="tabular-nums text-amber-700">{report.diagnostics.leads_skipped_no_campaign ?? 0}</strong></div>
                    <div>Skipped — unparseable date: <strong className="tabular-nums text-red-700">{report.diagnostics.leads_skipped_unparseable_date ?? 0}</strong></div>
                    <div>Skipped — no creator: <strong className="tabular-nums">{report.diagnostics.leads_skipped_no_creator ?? 0}</strong></div>
                  </div>
                  {Array.isArray(report.diagnostics.unparseable_date_samples) && report.diagnostics.unparseable_date_samples.length > 0 && (
                    <div className="mt-2 pt-2 border-t border-slate-200">
                      <div className="font-semibold text-slate-700 mb-1">Unparseable date samples (fix the CSV format or contact me):</div>
                      <ul className="font-mono text-[10px] space-y-0.5 max-h-32 overflow-y-auto">
                        {report.diagnostics.unparseable_date_samples.map((s, i) => (
                          <li key={i}>
                            <span className="bg-red-100 text-red-800 px-1.5 py-0.5 rounded-sm">{s.date_raw || "<empty>"}</span>
                            {" · "}{s.agent_name}{" · "}{s.campaign_name}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              {/* Nothing to do */}
              {(bf.updated ?? 0) === 0 && (prod.rows_to_create ?? 0) === 0 && (
                <div className="text-center text-xs text-muted-foreground py-6 italic" data-testid="repair-empty">
                  Nothing to repair. All bulk-uploaded leads already credit the right agent, and matching productivity rows exist.
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 p-4 border-t border-border bg-slate-50 rounded-b-md">
          <button onClick={onClose} data-testid="repair-cancel" className="btn-secondary text-xs">{committed ? "Close" : "Cancel"}</button>
          {!committed && report && (
            <button
              onClick={commit}
              disabled={committing || running || ((bf.updated ?? 0) === 0 && (prod.rows_to_create ?? 0) === 0)}
              data-testid="repair-commit"
              className="btn-primary text-xs inline-flex items-center gap-1 disabled:opacity-50"
            >
              {committing ? <Loader2 size={11} className="animate-spin" /> : <Check size={11} />}
              Commit changes
            </button>
          )}
          {committed && (
            <button onClick={onCommitted} data-testid="repair-done" className="btn-primary text-xs inline-flex items-center gap-1">
              <Check size={11} /> Done — reload table
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function SummaryCard({ label, value, accent = "slate", testid }) {
  const accentBg = {
    slate: "bg-slate-50 text-slate-900",
    amber: "bg-amber-50 text-amber-900",
    emerald: "bg-emerald-50 text-emerald-900",
  }[accent] || "bg-slate-50 text-slate-900";
  return (
    <div className={`${accentBg} rounded-sm p-3`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider opacity-70">{label}</div>
      <div className="text-2xl font-bold tabular-nums">{value}</div>
    </div>
  );
}
